import React, { useEffect, useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useSearchParams } from 'react-router-dom';
import { auth } from '../Firebase';
import { useAuth } from '../Context/AuthContext';
import {
  fetchUserFeedbackContext,
  submitFeedback,
} from '../utils/feedbackDeviceContext';
import feedbackIcon from '../Assets/Icons/feedback.png';
import './SubscribedFeedback.css';

export const UPGRADE_INFLUENCE_OPTIONS = [
  { value: 'custom_website', label: 'Custom website' },
  { value: 'guest_gallery', label: 'Guest gallery' },
  { value: 'digital_rsvp', label: 'Digital RSVPs' },
];

const COMMENTS_MAX = 2000;

function SubscribedFeedbackHeader() {
  return (
    <header className="subscribed-feedback-header">
      <div className="subscribed-feedback-header-inner">
        <div className="subscribed-feedback-header-badge-row">
          <img
            src={feedbackIcon}
            alt=""
            className="subscribed-feedback-header-icon"
          />
          <div className="subscribed-feedback-header-text">
            <p className="subscribed-feedback-header-title">Feedback</p>
            <p className="subscribed-feedback-header-subtitle">First Dance</p>
          </div>
        </div>
      </div>
    </header>
  );
}

function SubscribedFeedbackLoginModal({
  loginEmail,
  setLoginEmail,
  loginPassword,
  setLoginPassword,
  loginLoading,
  loginError,
  onSubmit,
}) {
  return (
    <div className="subscribed-feedback-login-overlay">
      <div className="subscribed-feedback-login-modal">
        <h2 className="subscribed-feedback-login-title">Login to continue</h2>
        <p className="subscribed-feedback-login-lead">
          Sign in with the same account you used on the billing page so we can
          save your feedback.
        </p>
        <form onSubmit={onSubmit}>
          <label htmlFor="subscribed-feedback-email" className="subscribed-feedback-login-label">
            Email
          </label>
          <input
            id="subscribed-feedback-email"
            type="email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            placeholder="Enter your email"
            required
            disabled={loginLoading}
            className="subscribed-feedback-login-input"
          />
          <label htmlFor="subscribed-feedback-password" className="subscribed-feedback-login-label">
            Password
          </label>
          <input
            id="subscribed-feedback-password"
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            placeholder="Enter your password"
            required
            disabled={loginLoading}
            className="subscribed-feedback-login-input"
          />
          {loginError && (
            <p className="subscribed-feedback-error" role="alert">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            className="subscribed-feedback-submit subscribed-feedback-login-submit"
            disabled={loginLoading}
          >
            {loginLoading ? 'Logging in…' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}

function SubscribedFeedbackLayout({ children, showLoginModal }) {
  return (
    <div
      className={`subscribed-feedback-page${showLoginModal ? ' subscribed-feedback-page--blurred' : ''}`}
    >
      <SubscribedFeedbackHeader />
      <main className="subscribed-feedback-body">{children}</main>
    </div>
  );
}

export default function SubscribedFeedback() {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email')?.trim() ?? '';

  const [influenceFeature, setInfluenceFeature] = useState('');
  const [comments, setComments] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState(emailParam);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [userContext, setUserContext] = useState({
    appVersion: '',
    planAtSubmission: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (emailParam) {
      setLoginEmail(emailParam);
    }
  }, [emailParam]);

  useEffect(() => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }

    // Either can be undefined to create mismatch
    // If both are undefined the submit will fail (no user)
    if (currentUser.email !== emailParam) {
      setShowLoginModal(true);
      setLoginError(
        'This account does not match the email on your billing link. Please sign in with the correct account.',
      );
      return;
    }

    setShowLoginModal(false);
    setLoginError('');
  }, [currentUser, emailParam]);

  const clientId = currentUser?.uid ?? null;

  useEffect(() => {
    if (!clientId) {
      setUserContext({ appVersion: '', planAtSubmission: '' });
      return undefined;
    }

    let cancelled = false;

    fetchUserFeedbackContext(clientId).then((context) => {
      if (!cancelled) {
        setUserContext(context);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const buildPayload = (context = userContext) => ({
    source: 'premium_upgrade_success',
    platform: 'web',
    appVersion: context.appVersion || '',
    planAtSubmission: context.planAtSubmission || '',
    answers: influenceFeature
      ? [
          {
            question: 'Which feature most influenced your upgrade today?',
            answer:
              UPGRADE_INFLUENCE_OPTIONS.find((o) => o.value === influenceFeature)
                ?.label ?? influenceFeature,
          },
        ]
      : [],
    comment: comments.trim(),
  });

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      await signInWithEmailAndPassword(auth, loginEmail.trim(), loginPassword);
    } catch (error) {
      const codeMap = {
        'auth/user-not-found': 'No account found with this email.',
        'auth/wrong-password': 'Incorrect password.',
        'auth/invalid-email': 'Invalid email address.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
      };
      setLoginError(codeMap[error.code] || 'Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!influenceFeature) {
      setErrorMessage('Please select the feature that influenced your upgrade.');
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setShowLoginModal(true);
      setErrorMessage('Please log in to share your feedback.');
      return;
    }

    setIsSubmitting(true);

    try {
      const authToken = await user.getIdToken();
      const context = await fetchUserFeedbackContext(user.uid);
      setUserContext(context);

      await submitFeedback(buildPayload(context), authToken);
      setSubmitted(true);
    } catch (error) {
      const status = error.response?.status;

      if (status === 403 || status === 498) {
        setShowLoginModal(true);
        setErrorMessage('Your session expired. Please log in again.');
        return;
      }

      const message =
        error.response?.data?.error ||
        error.message ||
        'Something went wrong while saving your feedback. Please try again.';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const loginModal = showLoginModal ? (
    <SubscribedFeedbackLoginModal
      loginEmail={loginEmail}
      setLoginEmail={setLoginEmail}
      loginPassword={loginPassword}
      setLoginPassword={setLoginPassword}
      loginLoading={loginLoading}
      loginError={loginError}
      onSubmit={login}
    />
  ) : null;

  if (submitted) {
    return (
      <>
        <SubscribedFeedbackLayout showLoginModal={showLoginModal}>
          <section className="subscribed-feedback-hero subscribed-feedback-hero--done">
            <h1 className="subscribed-feedback-headline">
              Thank you for your feedback
            </h1>
            <p className="subscribed-feedback-subcopy">
              Your thoughts help us build a better experience for couples like you.
            </p>
            <p className="subscribed-feedback-hint">
              You can close this tab and continue planning in the app.
            </p>
          </section>
        </SubscribedFeedbackLayout>
        {loginModal}
      </>
    );
  }

  return (
    <>
      <SubscribedFeedbackLayout showLoginModal={showLoginModal}>
        <section className="subscribed-feedback-hero">
          <h1 className="subscribed-feedback-headline">Thank you for upgrading</h1>
          <p className="subscribed-feedback-subcopy">
            We’d love a quick note on what made today the right time — it only takes a
            moment and helps us improve First Dance for everyone.
          </p>
        </section>

        <section className="subscribed-feedback-form-section">
          {errorMessage && (
            <p className="subscribed-feedback-error" role="alert">
              {errorMessage}
            </p>
          )}

          <form className="subscribed-feedback-form" onSubmit={handleSubmit} noValidate>
            <div className="subscribed-feedback-field">
              <label htmlFor="influenceFeature" className="subscribed-feedback-label">
                Which feature most influenced your upgrade today?
                <span className="subscribed-feedback-required">*</span>
              </label>
              <select
                id="influenceFeature"
                className="subscribed-feedback-select"
                value={influenceFeature}
                onChange={(e) => setInfluenceFeature(e.target.value)}
                disabled={showLoginModal}
              >
                <option value="">Select an option</option>
                {UPGRADE_INFLUENCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="subscribed-feedback-field">
              <label htmlFor="comments" className="subscribed-feedback-label">
                Anything you’d like to see on the app, or comments to help us improve?
              </label>
              <textarea
                id="comments"
                className="subscribed-feedback-textarea"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                maxLength={COMMENTS_MAX}
                rows={4}
                placeholder="Optional — features, ideas, or anything on your mind"
                disabled={showLoginModal}
              />
              <span className="subscribed-feedback-char-count">
                {comments.length}/{COMMENTS_MAX}
              </span>
            </div>

            <button
              type="submit"
              className="subscribed-feedback-submit"
              disabled={showLoginModal || isSubmitting}
            >
              {isSubmitting ? 'Sending…' : 'Share feedback'}
            </button>
          </form>
        </section>
      </SubscribedFeedbackLayout>
      {loginModal}
    </>
  );
}
